import spanish from "@angular/common/locales/es";
import french from "@angular/common/locales/fr";
import portugese from "@angular/common/locales/pt";
import enGb from "@angular/common/locales/en-GB";
import {registerLocaleData} from "@angular/common";
import {Component, HostListener, NgZone, OnDestroy, OnInit} from "@angular/core";
import {ActivatedRoute, NavigationEnd, NavigationStart, Router} from "@angular/router";
import {AuthenticationService} from "./authentication.service";
import {TenantService} from "./tenant.service";
import {TranslateService} from "@ngx-translate/core";
import {Store} from "@ngrx/store";
import {interval, Observable, Subject} from "rxjs";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {RefreshPromptComponent} from "./components/refresh-prompt/refresh-prompt.component";

import {
  AppActionTypes,
  RefreshedTokenReceived,
  UpdateCXoneTenant,
  UpdateLocale,
  UpdateLoginState,
  UpdateMinutesUntilTokenExpiration
} from "src/app/store/actions";
import {environment} from "src/environments/environment";
import {Actions, ofType} from "@ngrx/effects";
import {MessageService, PrimeNGConfig} from "primeng/api";
import {Tooltip} from "primeng/tooltip";
import {ResizeAwareService} from './services/resizeaware.service';
import {ValidateTenantUtils} from "./common/utils/validate.tenant.utils";
import {hash} from "./common/utils/hash";
import {ApiCredentialComponent} from "./components/api-credential/api-credential.component";
import {DialogOptions} from "./common/dialog-options";
import {WfmModalComponent as modalComponent} from "src/app/components/wfm-modal/wfm-modal.component";
import {MessageType, MFEBridgeService, MFEMessage} from './services/mfe-bridge.service';
import {SystemType} from './common/enums';
import {localeFallback} from './common/utils/localeFallback';

@Component({
  selector: "wfm-esp-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  providers: [MessageService]
})
export class AppComponent implements OnInit, OnDestroy {
  title = "wfm";
  isLoading = true;
  isLoadingLanguage = true;

  isError = false;
  isValidTenant = false;
  // Hides the top and sidebar by default
  showMainLayout = false;
  showMainLayoutCssObj: any = {"d-none": true};
  urlComesFromRcp = false;

  private refreshPromptThreshold = 16 * 60 * 1000;
  private refreshPromptCheckInterval = 1 * 60 * 1000;
  public isRefreshPrompt = false;

  private readonly supportedLocales = [spanish, french, portugese, enGb];
  private unsubscribe$: Subject<void> = new Subject();
  private window = window;

  public username = "-";
  public state$: Observable<any>;
  public isFocused:boolean = true;
  private tokenMinutesUntilExpiration = 0;
  isMenuExpanded: any = true;
  public menu: any = [];
  public cxOneLanguage: string;
  private canNotifyUserActivity = true;
  constructor(
    public router: Router,
    protected tenantService: TenantService,
    public store: Store<any>,
    public authenticationService: AuthenticationService,
    private translate: TranslateService,
    public modalService: NgbModal,
    private ngZone: NgZone,
    private messageService: MessageService,
    private resizeAwareService:ResizeAwareService,
    private action$: Actions,
    private activatedRoute: ActivatedRoute,
    private config: PrimeNGConfig,
    public mfeBridgeService: MFEBridgeService
  ) {
    translate.setDefaultLang("en-US");
    translate.use("en-US");
    this.registerSupportedLocales();
    this.state$ = this.store.select("state");
    this.isMenuExpanded = !(localStorage.getItem("isMenuExpanded")==="false");
  }

  ngOnInit() {
    let self = this;
    this.overrideTooltipDelay();
    this.state$.subscribe((state: any) => {
      this.updateMenuItems(state);
      this.updateShowMainLayout();
      console.log("isLoadingLanguage", state.isLoadingLanguage);
      this.isLoadingLanguage = state.isLoadingLanguage;
      if (state && state.user && state.user.username && state.user.username!=this.username) {
        this.username = state.user.username; //
        ValidateTenantUtils.recordUser(hash(this.username));
      }

      this.tokenMinutesUntilExpiration = state.tokenMinutesUntilExpiration;

    });

    this.action$.pipe(ofType(AppActionTypes.RefreshedTokenFailure))
    .subscribe(()=>{
          this.messageService.add({
            severity: "error",
            detail: this.translate.instant("refresh.prompt.failure",{x:this.tokenMinutesUntilExpiration})
          });
    });

    let samlSsoParameters = this.parseQueryParams();
    if ( samlSsoParameters.locale ) {
      // set translation locale for the pages that are displayed before authentication.
      this.setTranslateLocale(samlSsoParameters.locale);
    }

    if (samlSsoParameters.jtid && samlSsoParameters.locale) {
      this.retrieveAuthenticatedUser(samlSsoParameters.jtid, samlSsoParameters.locale);
    } else if (samlSsoParameters.errorCode === "1") {
      // navigate to unrecoverable-error page.  Do not call validateTenant()
      this.router.navigate(["/unrecoverable-error"]);
    } else if (samlSsoParameters.errorCode === "2") {
      // navigate to sso-login-error page.  Do not call validateTenant()
      this.router.navigate(["/sso-login-error"]);
    } else {
      // validating the tenant's subdomain from parsing the URL
      if(this.tenantService.isCXOne) {
        this.store.dispatch(new UpdateCXoneTenant(true));
      }

      this.validateTenant(self);
    }

    self.router.events.subscribe((routeEvent) => {
      if (routeEvent instanceof NavigationStart) {
        if (this.router.url === "/") {
          var token = this.getUrlSearchParams("token");
          var locale = this.getLocaleFromWfmLocale(this.getUrlSearchParams("locale"));
          var lineOfBusiness = this.getLobFromWfmLocale(this.getUrlSearchParams("locale"));
          if (token) {
            this.authenticationService.ssoToken = token;
            this.authenticationService.ssoLocale = locale;
            this.authenticationService.ssoLineOfBusiness = lineOfBusiness;
            this.urlComesFromRcp = true;
          }
        }
      }
      if (routeEvent instanceof NavigationEnd) {
        this.showMainLayout = routeEvent.urlAfterRedirects!="/" && routeEvent.urlAfterRedirects.indexOf("login") < 0
          && routeEvent.urlAfterRedirects.indexOf("logout") < 0 && routeEvent.urlAfterRedirects.indexOf("unrecoverable-error") < 0
          && routeEvent.urlAfterRedirects.indexOf("sso-login-error") < 0;
      }
    });

    this.translate.stream("primeng").subscribe(res => {
      this.config.setTranslation(res)
    });

    this.mfeBridgeService.receiveMessages().subscribe((message) => {
     this.handleMfeMessage(message);
    });
    this.mfeBridgeService.initiateHandshake();

    // start token refresh timer
    this.runOutsideNgZone();
  }

  private setTranslateLocale(locale: string) {
    // set translation locale
    let theLocale = localeFallback(locale);
    this.translate.use(theLocale);
}

  private parseQueryParams() {
    // need to parse both the jtid and locale
    if (this.router.url === "/") {
      let jtidVal = this.getUrlSearchParams("jtid");
      let localeVal = this.getUrlSearchParams("locale");
      let errorCode = this.getUrlSearchParams("errorCode");
      return {jtid: jtidVal, locale: localeVal, errorCode: errorCode};
    } else {
      return {};
    }
  }

  private runOutsideNgZone() {
    this.ngZone.runOutsideAngular(() => {
      //const source = interval(10000);
      const source = interval(this.refreshPromptCheckInterval);
      const subscribe = source.subscribe(val => {
        this.ngZone.run(() => {
          if (this.authenticationService.hasToken && this.tenantService.isCXOne == false) {
            let timeUntilTokenExpiration: number = this.authenticationService.getTimeUntilTokenExpiration();
            this.store.dispatch(new UpdateMinutesUntilTokenExpiration(Math.floor(timeUntilTokenExpiration / 60000)));
            if (timeUntilTokenExpiration < this.refreshPromptCheckInterval) {
              this.isRefreshPrompt = false;
              this.modalService.dismissAll();
              this.logout();
            } else if (timeUntilTokenExpiration < this.refreshPromptThreshold) {
              //} else if(true){
              this.showRefreshPromptDialog();
            }
          }
        });
      });
    });
  }

  private validateTenant(self: this) {
    let tenantName = this.getUrlSearchParams('tenant'); //for debugging purposes
    self.tenantService.validate(tenantName).then((data: any) => {
      self.isValidTenant = true;
      self.isLoading = false;



      if(this.tenantService.isCXOne) {
        ValidateTenantUtils.systemType = "CXONE";
        ValidateTenantUtils.recordTenant(data.subdomain);
        return;
      }else{
        ValidateTenantUtils.recordTenant(data.subdomain);
      }
      if (this.urlComesFromRcp || !data.ssoEnabled) this.espAuthentication(); // ESP authentication.
      else this.redirectToIdpAuthentication(data.wfmSamlEspLoginUrl); // IDP authentication

    }).catch((response) => {
      self.isLoading = false;
      self.isError = true;
      self.isValidTenant = false;
      this.showMainLayout = false;
      if ( response.status === 503 ) {
        // SSO login error.  Show sso-login-error page
        self.isError = false;
        this.router.navigate(["/sso-login-error"]);
      }
    });
  }

  redirectToIdpAuthentication(wfmSamlEspLoginUrl: string) {
    // If user is authenticated we are not going to IDP.
    if (!this.authenticationService.isAuthorized()) {
      this.window.location.href = wfmSamlEspLoginUrl;
    }
  }

  retrieveAuthenticatedUser(jtid: string, locale: string) {
    this.authenticationService.retrieveAuthenticatedUser(jtid).then(data => {
      return this.authenticationService.entityList();
    }).then(() => {
      this.ifAuthorized();
    }).catch(err => {
      console.log("app component error", err);
    });
  }

  espAuthentication() {
    if (this.router.url === "/") {
      if (this.authenticationService.isAuthorized()) {
        this.ifAuthorized();
      } else if (this.authenticationService.ssoToken) {
        this.logUserInSSO();
      } else {
        this.ifNotAuthorized();
      }
    }
  }

  public updateMenuItems(state: any) {
    if (state && state.isLoggedIn) {
      this.showMainLayout = state.isLoggedIn;
      this.loadMenuItems();
    } else {
      this.showMainLayout = false;
    }
  }
  private loadMenuItems() {
    this.menu = [
      { key: "plan.page.title", icon: "icon icon-intraday", route: "plans" },
      { key: "imported.forecasts.page.title", icon: "icon icon-file-directory-create", route: "imported-forecasts" },
      { key: "historical-data.page.title", icon: "icon icon-history_data", route: "historical-data" }
    ];
  }


  registerSupportedLocales(){
    this.supportedLocales.forEach((l) => registerLocaleData(l));

  }

  /** WFM concatenates the locale and the line of business, like this: en_US_cc
   In ESP, we break these apart and use them separately. **/
  getLocaleFromWfmLocale(wfmLocale) {
    return wfmLocale.slice(0, 5).replace("_", "-");
  }

  getLobFromWfmLocale(wfmLocale) {
    return wfmLocale.slice(-2);
  }


  showRefreshPromptDialog() {
    if (!this.isRefreshPrompt) {
      this.isRefreshPrompt = true;
      const modalRef = this.modalService.open(RefreshPromptComponent, {
        centered: true,
        windowClass: "b-info wfm-modal",
        container: "div.app-wrapper",
        backdropClass: "wfm-modal-backdrop",
        backdrop: "static",
        keyboard: false
      }).result.then((data) => {
        this.isRefreshPrompt = false;
      }, (reason) => {
        this.isRefreshPrompt = false;
        this.modalService.dismissAll();
        this.logout();
      });
    }
  }

  getUrlSearchParams(name:string) {
    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    var regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
    var results = regex.exec(this.window.location.search);
    return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
  };

  updateShowMainLayout() {
    this.showMainLayoutCssObj = {
      "d-none": !this.showMainLayout,
      "d-flex": this.showMainLayout,
      "show-mini-sidebar": !this.isMenuExpanded
    };
    this.resizeAwareService.NotifyResize();
  }

  async logout() {
    if (await this.router.navigate(["/logout"])) {
      this.authenticationService.logout()
    }
  }

  public showApiCredentialsDialog() {
    const modalRef = this.modalService.open(ApiCredentialComponent, {
      centered: true,
      windowClass: "b-info wfm-modal api-keys",
      container: "body",
      backdrop: "static",
      keyboard: false,
      modalDialogClass: "api-keys-dialog"
    });

    modalRef.result.then((data) => {
      console.debug("successfully closed the API credentials dialog");
    }, (reason) => {
      if (reason?.retrieveError) {
        // show error dialog
        const dgOption: DialogOptions = new DialogOptions();
        dgOption.titleKey = this.translate.instant("api-credentials.retrieve.error.title");
        dgOption.messageKey = this.translate.instant("api-credentials.retrieve.error.msg");
        dgOption.msgType = "error";
        dgOption.showCancel = false;
        dgOption.confirmLabel = "btn.close";
        dgOption.container = "body";

        modalComponent.showModalMessage(dgOption, this.modalService)
          .then(() => { console.debug("error dialog successfully handled")})
          .catch(err => {
            console.error("error in API creds error dialog box", err);
          });
      }
    });

    return modalRef;
  }

  public isPublicApiFeatureEnabled() {
    return this.authenticationService.userData?.publicApiFeature;
  }

  public showApiCredentialsMenuItem() {
    return this.authenticationService.userData?.userRoles?.includes("ESP_ADMIN_ROLE");
  }

  logUserInSSO() {
    this.isLoading = true;
    let self = this;
    this.authenticationService.ssoAuthenticate(self.tenantService.tenantIdentifier)
      .then(
        data => {
          self.authorizeUserSSO();
        })
      .catch(
        err => {
          self.isLoading = false;
          self.ifNotAuthorized();
        });
  }

  authorizeUserSSO() {
    let self = this;
    return this.authenticationService.authorizeUser()
      .then(
        data => {
          self.isLoading = false;
          self.ifAuthorized();
        }
      )
      .catch(function (err) {
        self.isLoading = false;
      });
  }

  ifAuthorized() {
    this.showMainLayout = true;
    this.isRefreshPrompt = false;
    this.router.navigate(["/plans"]);
  }

  ifNotAuthorized() {
    this.router.navigate(["/login"]);
  }

  gotoHelp(){
    let helpUrl = environment.espHelpUrl;
    let language = this.translate.currentLang ? this.translate.currentLang.substring(0,2) : environment.defaultLocale.substring(0, 2);

    helpUrl =`${environment.espHelpUrl}/${language}/content/home.htm`;
    this.window.open(helpUrl);
  }

  @HostListener("window:storage", ["$event"])
  storageListener(e: any) {
    // this.isFocused is used because:
    // IE11 fires this event on the same window/tab as well
    // document.hidden does not work on IE11 when multiple window is opened.
    if (!this.isFocused && e.key === "userData" && (e.newValue === null || e.newValue === "")) {
      this.modalService.dismissAll();
      this.logout();
    }
  }

  @HostListener('window:focus', ['$event'])
  onFocus(event: any): void {
    this.isFocused = true;
  }

  @HostListener('window:blur', ['$event'])
  onBlur(event: any): void {
    this.isFocused = false;
  }
  @HostListener("window:orientationchange", ["$event"])
  onOrientationChange(event: any): void {
    this.resizeAwareService.NotifyResize();
  }
  handleMenuItemClickEvent($event: any) {

  }

  onSidebarToggle() {
    //let self = this;
    //self.isMenuWidthTransitioning=true;
    //setTimeout(function() {self.isMenuWidthTransitioning=false;},800);
    this.isMenuExpanded=!this.isMenuExpanded;
    this.updateShowMainLayout();
    localStorage.setItem("isMenuExpanded",this.isMenuExpanded.toString())
  }

  private overrideTooltipDelay() {
    let tooltipBase:any = Tooltip;
    let getOptions = tooltipBase.prototype.getOption;
    tooltipBase.prototype.getOption = function getOption(option){
      if(option==="showDelay"){
        return 500;
      }else{
        return getOptions.call(this,option);
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  @HostListener('window:click', ['$event'])
  @HostListener('window:keypress', ['$event'])
  @HostListener('window:scroll', ['$event'])
  onWindowClick(event: any): void {
    //notification is being throttled to avoid too many notifications
    if (this.tenantService.isCXOne && this.canNotifyUserActivity) {
      this.mfeBridgeService.sendMessage(null, MessageType.ACTIVITY);
      this.canNotifyUserActivity = false;
      setTimeout(() => {
        this.canNotifyUserActivity = true;
      }, 1000);
    }
  }

  async handleMfeMessage(message: MFEMessage) {
    console.log('ESP Received message from CXone', message);
    if (message.type === MessageType.NAVIGATION) {
      let navigationResult = await this.router.navigate([message.data]);
      if (navigationResult) {
        this.mfeBridgeService.sendMessage(null, MessageType.NAVIGATION_SUCCESS);
      } else {
        this.mfeBridgeService.sendMessage(this.router.url, MessageType.NAVIGATION_REJECTED);
      }
    } else if (message.type === MessageType.AUTH) {
      let cxOneUserInfo = message.data.userInfo;
      let navigationPath = message.data.path;
      this.cxOneLanguage = this.convertCxOneLanguage(message.data.language) || 'en-US';
      try {
        await this.authenticationService.cxOneAuthenticate(cxOneUserInfo.userName, cxOneUserInfo.token, this.cxOneLanguage);
        await this.authenticationService.authorizeUser(SystemType.CXONE);
        this.store.dispatch(new UpdateLoginState(true));
        await this.router.navigate([navigationPath])
        this.mfeBridgeService.sendMessage(null, MessageType.AUTH);
      } catch (e) {
        this.mfeBridgeService.sendMessage(null, MessageType.ERROR);
      }
    } else if (message.type === MessageType.REFRESH_TOKEN) {
      let cxOneUserInfo = message.data;
      console.log('CXone user info - Refreshed', cxOneUserInfo);

      try {
        await this.authenticationService.cxOneAuthenticate(cxOneUserInfo.userName, cxOneUserInfo.token, this.cxOneLanguage);
        this.store.dispatch(new RefreshedTokenReceived(null));
      } catch (e) {
        this.mfeBridgeService.sendMessage(null, MessageType.REFRESH_TOKEN_ERROR);
      }
    }
  }

  convertCxOneLanguage(language) {
    let lang = language.toLowerCase().split('-')[0].trim();

    if (lang === 'en') {
      // Check browser language locale for English
      const browserLang = navigator.language || (navigator as any).userLanguage;
      if (browserLang && browserLang.toLowerCase() === 'en-gb') {
        return 'en-GB';
      }

      return 'en-US';
    } else if (lang === 'es') {
      return 'es-ES';
    } else if (lang === 'fr') {
      return 'fr-FR';
    } else if (lang === 'pt') {
      return 'pt-BR';
    }
    return null;
  }
}
